import React, { useEffect, useState } from "react"
import QRCode from "react-qr-code"
import { CheckCircleIcon, XMarkIcon } from "@heroicons/react/24/outline"
import Button from "aldoo-ra/Button"
import useT from "../hooks/useT"
import { AppleStoreButton, GoogleStoreButton } from "../ui/AppsIcons"
import { LicenseCardPairStatus } from "../api"

const STEP = { subscription: 1, install: 2, activate: 3, done: 4 }

/**
 * License card activation (old activateLicenseDialog.jsx): needs a premium
 * subscription → install Aldoo Kids → scan the QR of `/license?l=<lcid>` with
 * the phone while the dialog polls `license-card {pair_status}` every second
 * → congratulations.
 */
export default function ActivateLicenseDialog({ license, canActivate, mobile, open, onClose, onPaired }) {
  const t = useT()
  const [step, setStep] = useState(STEP.install)
  const activateUrl = typeof window === "undefined" ? "" : `${window.location.origin}/license?l=${license?.lcid}`

  useEffect(() => {
    if (!open) return
    if (!canActivate) return setStep(STEP.subscription)
    if (license?.paired) return setStep(STEP.done)
    setStep(STEP.install)
  }, [open, canActivate, license?.paired])

  useEffect(() => {
    if (!open || step !== STEP.activate || !license?.lcid) return
    const interval = setInterval(async () => {
      const res = await LicenseCardPairStatus(license.lcid).catch(() => null)
      if (!res?.paired) return
      clearInterval(interval)
      setStep(STEP.done)
      onPaired?.(license)
    }, 1000)
    return () => clearInterval(interval)
  }, [open, step, license?.lcid])

  if (!open || !license) return null

  return (
    <div className="fixed inset-0 z-[550] flex items-center justify-center bg-black/60 p-4" data-testid="activate-license-dialog" data-step={step}>
      <div role="dialog" aria-modal="true" className="relative w-full max-w-[560px] rounded-[16px] bg-paper p-6 pt-12 flex flex-col items-center gap-5 text-center">
        <button type="button" onClick={onClose} aria-label={t`Close`} className="absolute right-4 top-4 p-1 text-text_secondary hover:text-text">
          <XMarkIcon className="w-6 h-6" />
        </button>

        {step === STEP.subscription && (
          <>
            <h3 className="m-0 text-[22px] font-bold text-primary">{t`lc_you_need_subscription`}</h3>
            <Button variant="primary_l" href="/plans">{t`Subscribe Now`}</Button>
          </>
        )}

        {step === STEP.install && (
          <>
            <h3 className="m-0 text-[22px] font-bold text-primary">{t`lc_first_install_the_app`}</h3>
            <div className="flex flex-row flex-wrap items-center justify-center gap-4">
              <AppleStoreButton app="kids" width={150} />
              <GoogleStoreButton app="kids" width={150} />
            </div>
            <Button variant="primary_l" onClick={() => setStep(STEP.activate)} data-testid="i-have-the-app">
              {t`lc_i_have_the_app`}
            </Button>
          </>
        )}

        {step === STEP.activate && (
          <>
            <h3 className="m-0 text-[22px] font-bold text-primary">{t`Activate License`}</h3>
            <div className="rounded-lg bg-white p-3" data-testid="license-qr">
              <QRCode size={160} value={activateUrl} />
            </div>
            <p className="m-0 text-[15px]">{t`Scan the QR Code with your Mobile device`}</p>
            {mobile && (
              <div className="flex flex-col items-center gap-3">
                <span className="text-[14px] text-text_secondary">{t`or tap below`}</span>
                <a href={activateUrl} target="_blank" rel="noopener noreferrer">
                  <Button variant="primary_l">{t`lc_activate`}</Button>
                </a>
              </div>
            )}
          </>
        )}

        {step === STEP.done && (
          <>
            <CheckCircleIcon className="w-20 h-20 text-primary" />
            <h3 className="m-0 text-[22px] font-bold text-primary">{t`lc_your_license_is_now_active`}</h3>
            <div className="flex flex-row flex-wrap items-center justify-center gap-4">
              <AppleStoreButton app="kids" width={150} />
              <GoogleStoreButton app="kids" width={150} />
            </div>
            <p className="m-0 text-[15px]">{t`lc_you_can_now_play_on_your_device`}</p>
            <Button variant="primary_l" onClick={onClose}>
              {t`Ok`}
            </Button>
          </>
        )}
      </div>
    </div>
  )
}
